import { useCallback } from 'react';
import { getMissionQuota, type ViduV1ReportAdEventRequest } from '@/app/apis';
import { useStore } from '@/app/providers/store';
import { useFetch } from './use-fetch';
import { useGlobalModal } from './use-global-modal';

export type AdMissionType = Extract<
  NonNullable<ViduV1ReportAdEventRequest['mission_type']>,
  'watch_ad_credit' | 'watch_ad_watermark'
>;

export const useAdsDialog = () => {
  const { open: openNoWatermarkAdsDialog } = useGlobalModal('noWatermarkAdsDialog');
  const { open: openInsufficientCreditsAdsDialog } = useGlobalModal('insufficientCreditsAdsDialog');
  const userInfo = useStore((state) => state.userInfo);

  const { runAsync: fetchQuota, loading } = useFetch(getMissionQuota, {
    manual: true,
  });

  /**
   * 检查指定场景是否可以显示广告
   * 由后端判断用户资格，前端只需关注返回的 eligible 字段
   */
  const checkEligible = useCallback(
    async (scene: AdMissionType): Promise<boolean> => {
      if (!userInfo) return false;

      try {
        const result = await fetchQuota({ query: { mission_type: scene } });
        return result?.data?.eligible ?? false;
      } catch {
        return false;
      }
    },
    [fetchQuota, userInfo],
  );

  /**
   * 尝试打开去水印广告挽留弹窗
   * @param creationId 创作 ID，用于获取无水印视频
   * @param onClose 关闭回调
   */
  const tryOpenNoWatermarkDialog = useCallback(
    async (creationId?: string, onClose?: () => void): Promise<boolean> => {
      const eligible = await checkEligible('watch_ad_watermark');
      if (!eligible) return false;

      openNoWatermarkAdsDialog({ creationId, onClose: onClose ?? (() => {}) });
      return true;
    },
    [checkEligible, openNoWatermarkAdsDialog],
  );

  /**
   * 尝试打开积分不足广告挽留弹窗
   */
  const tryOpenInsufficientCreditsDialog = useCallback(
    async (onClose?: () => void): Promise<boolean> => {
      const eligible = await checkEligible('watch_ad_credit');
      if (!eligible) return false;

      openInsufficientCreditsAdsDialog({ onClose: onClose ?? (() => {}) });
      return true;
    },
    [checkEligible, openInsufficientCreditsAdsDialog],
  );

  return {
    loading,
    checkEligible,
    tryOpenNoWatermarkDialog,
    tryOpenInsufficientCreditsDialog,
  };
};
